/* eslint-disable no-continue */
/**
 * Prompt Template DSL — canonical implementation (parser + renderer + validator)
 *
 * Implements a template language supporting:
 * - Parameter replacement: {{param-name}}
 * - Tag-based conditionals: {{if #tag and #tag}}...{{else}}...{{endif}}
 * - Probability-based conditionals: {{prob 0.5}}...{{else}}...{{endprob}}
 * - Nested blocks with proper {{else}} binding
 * - Fenced code blocks (```...```) and JSON-like directives ({{"key": ...}})
 *   are preserved as literal text
 *
 * Key Features:
 * - Double curly brackets {{...}} distinguish template directives from JSON syntax
 * - Tags are case-insensitive and trimmed
 * - Conditions support only 'and' or 'or' (no mixing)
 * - Probabilities in range (0, 1], each block draws independently
 * - Proper nesting and AST construction
 *
 * SINGLE SOURCE OF TRUTH (issue #738):
 * The canonical source of this file is shared/src/promptTemplate.ts.
 * A byte-identical copy lives at
 * cloud-run/generation-service/services/promptTemplateParser.ts because the
 * Cloud Run build cannot resolve root shared/ imports at runtime (tsc path
 * aliases are compile-time only). A drift-guard test
 * (services/promptTemplateParser.sync.test.ts) fails if the copies diverge.
 * To change the DSL: edit shared/src/promptTemplate.ts, then run
 *   cp shared/src/promptTemplate.ts cloud-run/generation-service/services/promptTemplateParser.ts
 *
 * Consumers:
 * - Cloud Run textGenService: parsePromptTemplate (authoritative rendering)
 * - Admin UI Prompt Debugger: validatePromptTemplate (save-time validation)
 *   validatePromptTemplate is derived from the parser, so the validator
 *   accepts exactly what the parser accepts.
 *
 * @module promptTemplate
 * @version 2.2
 */

/**
 * Template node types in the AST
 */
type TemplateNode =
  | { type: 'text'; content: string }
  | { type: 'param'; name: string }
  | {
      type: 'if';
      condition: Condition;
      thenNodes: TemplateNode[];
      elseNodes: TemplateNode[];
    }
  | {
      type: 'prob';
      probability: number;
      thenNodes: TemplateNode[];
      elseNodes: TemplateNode[];
    };

/**
 * Tag-based condition
 */
interface Condition {
  tags: string[];
  operator: 'and' | 'or';
}

/**
 * Result of template validation
 */
export interface PromptTemplateValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Parse and render a prompt template with parameters and tags
 *
 * This parser is the authoritative implementation - it validates syntax during parsing
 * and throws errors for malformed templates.
 *
 * @param template - Template string with directives
 * @param params - Parameter values for {{param-name}} replacement
 * @param tags - Array of tag strings for {{if}} conditions
 * @returns Rendered prompt string
 * @throws Error if template is malformed
 */
export function parsePromptTemplate(
  template: string,
  params: Record<string, string> = {},
  tags: string[] = []
): string {
  const parser = new TemplateParser(template);
  const ast = parser.parse();
  return evaluateNodes(ast, params, tags);
}

/**
 * Validate a prompt template for syntax errors without rendering it.
 *
 * Runs the canonical parser (parse only — no parameter substitution, no
 * probability draws), so a template is valid if and only if
 * parsePromptTemplate would accept it.
 *
 * @param template - Template string to validate
 * @returns Object with `valid` boolean and optional `error` message
 *
 * @example
 * ```typescript
 * const result = validatePromptTemplate('{{if #happy}}Hello{{endif}}');
 * if (!result.valid) {
 *   console.error(result.error);
 * }
 * ```
 */
export function validatePromptTemplate(
  template: string
): PromptTemplateValidationResult {
  if (!template || template.trim() === '') {
    return { valid: true }; // Empty template is valid
  }

  try {
    const parser = new TemplateParser(template);
    parser.parse();
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error:
        error instanceof Error ? error.message : 'Unknown validation error',
    };
  }
}

/**
 * Template parser class
 */
class TemplateParser {
  private template: string;

  private pos: number = 0;

  private length: number;

  constructor(template: string) {
    this.template = template;
    this.length = template.length;
  }

  /**
   * Parse template into AST
   */
  parse(): TemplateNode[] {
    const nodes: TemplateNode[] = [];

    while (this.pos < this.length) {
      // Preserve fenced code blocks (e.g., ```json ... ```) as literal text
      if (this.startsWithCodeFence()) {
        const fenceText = this.consumeCodeFence();
        nodes.push({ type: 'text', content: fenceText });
        continue;
      }

      // Check for directive
      if (this.template.startsWith('{{', this.pos)) {
        const node = this.parseDirective();
        if (node) {
          nodes.push(node);
        }
      } else {
        // Parse text until next directive
        const text = this.parseText();
        if (text) {
          nodes.push({ type: 'text', content: text });
        }
      }
    }

    return nodes;
  }

  /**
   * Detect if the current position starts a fenced code block
   */
  private startsWithCodeFence(): boolean {
    return this.template.startsWith('```', this.pos);
  }

  /**
   * Consume a fenced code block (including the fences) as plain text.
   * If a closing fence is not found, consume to the end of the template.
   */
  private consumeCodeFence(): string {
    const start = this.pos;
    // Skip the opening fence
    this.pos += 3;

    // Skip optional language identifier
    while (this.pos < this.length && this.peek() !== '\n') {
      this.pos += 1;
    }
    // Consume the newline after language (if present)
    if (this.peek() === '\n') this.pos += 1;

    // Find closing fence
    while (this.pos < this.length && !this.template.startsWith('```', this.pos)) {
      this.pos += 1;
    }

    // Include closing fence if present
    if (this.template.startsWith('```', this.pos)) {
      this.pos += 3;
    }

    return this.template.slice(start, this.pos);
  }

  /**
   * Parse text content until next {{
   */
  private parseText(): string {
    let text = '';
    while (this.pos < this.length && !this.template.startsWith('{{', this.pos)) {
      text += this.advance();
    }
    return text;
  }

  /**
   * Parse a directive: {{param}}, {{if}}, {{prob}}, {{else}}, {{endif}}, {{endprob}}
   */
  private parseDirective(): TemplateNode | null {
    if (!this.template.startsWith('{{', this.pos)) {
      throw new Error('Expected {{');
    }
    this.pos += 2; // Skip {{

    // Find matching }}
    const start = this.pos;
    let depth = 1;
    let end = this.pos;

    while (end < this.length - 1 && depth > 0) {
      if (this.template.startsWith('{{', end)) {
        depth++;
        end += 2;
      } else if (this.template.startsWith('}}', end)) {
        depth--;
        if (depth > 0) end += 2;
      } else {
        end++;
      }
    }

    if (depth !== 0) {
      throw new Error(`Unmatched {{ at position ${this.pos - 2}`);
    }

    const content = this.template.slice(start, end).trim();
    this.pos = end + 2; // Skip }}

    if (!content) {
      return null; // Empty directive
    }

    // Parse directive content
    const tokens = TemplateParser.tokenizeDirective(content);
    if (tokens.length === 0) {
      return null;
    }

    // If this looks like a JSON snippet (e.g., {"shows": [...]}) or key/value example,
    // treat it as literal text so examples in prompts are preserved.
    if (/"[^"]*"\s*:/.test(content)) {
      return { type: 'text', content: `{{${content}}}` };
    }

    const firstToken = tokens[0]!.toLowerCase();

    // Reserved keywords
    if (firstToken === 'if') {
      return this.parseIfBlock(tokens.slice(1).join(' ').trim());
    }

    if (firstToken === 'prob') {
      return this.parseProbBlock(tokens);
    }

    if (firstToken === 'else' || firstToken === 'endif' || firstToken === 'endprob') {
      throw new Error(`Unexpected ${firstToken} without matching opening block`);
    }

    // Parameter
    return { type: 'param', name: content };
  }

  /**
   * Tokenize directive content by whitespace
   */
  private static tokenizeDirective(content: string): string[] {
    return content.split(/\s+/).filter(Boolean);
  }

  /**
   * Parse {if} block
   */
  private parseIfBlock(conditionStr: string): TemplateNode {
    const condition = TemplateParser.parseCondition(conditionStr);
    const thenNodes: TemplateNode[] = [];
    const elseNodes: TemplateNode[] = [];

    // Parse then branch
    let inElse = false;
    let foundEnd = false;
    while (this.pos < this.length) {
      // Check for control directives
      if (this.template.startsWith('{{', this.pos)) {
        const directive = this.peekDirective();

        if (directive === 'else') {
          this.consumeDirective('else'); // Consume {{else}}
          inElse = true;
          continue;
        }

        if (directive === 'endif') {
          this.consumeDirective('endif'); // Consume {{endif}}
          foundEnd = true;
          break;
        }

        // Not a control directive, parse as normal
        const node = this.parseDirective();
        if (node) {
          if (inElse) {
            elseNodes.push(node);
          } else {
            thenNodes.push(node);
          }
        }
      } else {
        // Parse text
        const text = this.parseText();
        if (text) {
          if (inElse) {
            elseNodes.push({ type: 'text', content: text });
          } else {
            thenNodes.push({ type: 'text', content: text });
          }
        }
      }
    }

    if (!foundEnd) {
      throw new Error('Unclosed if block');
    }

    return {
      type: 'if',
      condition,
      thenNodes,
      elseNodes,
    };
  }

  /**
   * Parse {prob} block
   */
  private parseProbBlock(tokens: string[]): TemplateNode {
    if (tokens.length < 2) {
      throw new Error('prob directive missing probability argument');
    }

    const probStr = tokens[1];
    if (!probStr) {
      throw new Error('prob directive missing probability argument');
    }
    const probability = parseFloat(probStr);

    if (Number.isNaN(probability)) {
      throw new Error(`Invalid probability: ${probStr}`);
    }

    if (probability <= 0 || probability > 1) {
      throw new Error(`Probability must be in (0, 1], got ${probability}`);
    }

    if (tokens.length > 2) {
      throw new Error(`prob directive has extra tokens: ${tokens.slice(2).join(' ')}`);
    }

    const thenNodes: TemplateNode[] = [];
    const elseNodes: TemplateNode[] = [];

    // Parse branches
    let inElse = false;
    let foundEnd = false;
    while (this.pos < this.length) {
      // Check for control directives
      if (this.template.startsWith('{{', this.pos)) {
        const directive = this.peekDirective();

        if (directive === 'else') {
          this.consumeDirective('else'); // Consume {{else}}
          inElse = true;
          continue;
        }

        if (directive === 'endprob') {
          this.consumeDirective('endprob'); // Consume {{endprob}}
          foundEnd = true;
          break;
        }

        // Not a control directive, parse as normal
        const node = this.parseDirective();
        if (node) {
          if (inElse) {
            elseNodes.push(node);
          } else {
            thenNodes.push(node);
          }
        }
      } else {
        // Parse text
        const text = this.parseText();
        if (text) {
          if (inElse) {
            elseNodes.push({ type: 'text', content: text });
          } else {
            thenNodes.push({ type: 'text', content: text });
          }
        }
      }
    }

    if (!foundEnd) {
      throw new Error('Unclosed prob block');
    }

    return {
      type: 'prob',
      probability,
      thenNodes,
      elseNodes,
    };
  }

  /**
   * Parse tag-based condition
   */
  private static parseCondition(conditionStr: string): Condition {
    if (!conditionStr) {
      throw new Error('Empty condition in if directive');
    }

    // Check for trailing operator FIRST
    if (conditionStr.trim().endsWith(' and') || conditionStr.trim().endsWith(' or')) {
      throw new Error('Condition ends with operator');
    }

    // Split by 'and' or 'or'
    let operator: 'and' | 'or';
    let parts: string[];

    if (conditionStr.includes(' and ')) {
      if (conditionStr.includes(' or ')) {
        throw new Error('Cannot mix and/or in same condition');
      }
      operator = 'and';
      parts = conditionStr.split(' and ').map(s => s.trim());
    } else if (conditionStr.includes(' or ')) {
      operator = 'or';
      parts = conditionStr.split(' or ').map(s => s.trim());
    } else {
      // Single tag
      operator = 'and';
      parts = [conditionStr.trim()];
    }

    // Validate and extract tags
    const tags: string[] = [];
    for (const part of parts) {
      if (!part.startsWith('#')) {
        throw new Error(`Condition term must start with #, got: ${part}`);
      }
      const tag = part.slice(1).trim();
      if (!tag) {
        throw new Error('Empty tag name after #');
      }
      if (tag.includes(' ')) {
        throw new Error(`Tag name cannot contain spaces: ${tag}`);
      }
      tags.push(tag);
    }

    if (tags.length === 0) {
      throw new Error('Condition has no tags');
    }

    return { tags, operator };
  }

  /**
   * Peek at next directive without consuming
   */
  private peekDirective(): string | null {
    if (!this.template.startsWith('{{', this.pos)) return null;

    let pos = this.pos + 2;
    let depth = 1;

    while (pos < this.length - 1 && depth > 0) {
      if (this.template.startsWith('{{', pos)) {
        depth++;
        pos += 2;
      } else if (this.template.startsWith('}}', pos)) {
        depth--;
        if (depth > 0) pos += 2;
      } else {
        pos++;
      }
    }

    const content = this.template.slice(this.pos + 2, pos).trim();
    const firstToken = content.split(/\s+/)[0];
    return firstToken ? firstToken.toLowerCase() : null;
  }

  /**
   * Peek at current character
   */
  private peek(): string {
    return this.template[this.pos] || '';
  }

  /**
   * Advance position and return character
   */
  private advance(): string {
    return this.template[this.pos++] || '';
  }

  /**
   * Consume a specific directive (e.g., 'else', 'endif')
   */
  private consumeDirective(expected: string): void {
    if (!this.template.startsWith('{{', this.pos)) {
      throw new Error(`Expected {{ for ${expected}`);
    }
    this.pos += 2; // Skip {{

    // Find matching }}
    let depth = 1;
    let end = this.pos;

    while (end < this.length - 1 && depth > 0) {
      if (this.template.startsWith('{{', end)) {
        depth++;
        end += 2;
      } else if (this.template.startsWith('}}', end)) {
        depth--;
        if (depth > 0) end += 2;
      } else {
        end++;
      }
    }

    this.pos = end + 2; // Skip past }}
  }
}

/**
 * Evaluate AST nodes to produce final string
 */
function evaluateNodes(
  nodes: TemplateNode[],
  params: Record<string, string>,
  tags: string[]
): string {
  let result = '';

  // Normalize tags to lowercase for case-insensitive matching
  const normalizedTags = tags.map(t => t.trim().toLowerCase());

  for (const node of nodes) {
    if (node.type === 'text') {
      result += node.content;
    } else if (node.type === 'param') {
      result += params[node.name] ?? '';
    } else if (node.type === 'if') {
      const condition = evaluateCondition(node.condition, normalizedTags);
      const branch = condition ? node.thenNodes : node.elseNodes;
      result += evaluateNodes(branch, params, tags);
    } else if (node.type === 'prob') {
      const hit = Math.random() < node.probability;
      const branch = hit ? node.thenNodes : node.elseNodes;
      result += evaluateNodes(branch, params, tags);
    }
  }

  return result;
}

/**
 * Evaluate tag-based condition
 */
function evaluateCondition(condition: Condition, normalizedTags: string[]): boolean {
  const results = condition.tags.map(tag => {
    const normalizedTag = tag.trim().toLowerCase();
    return normalizedTags.includes(normalizedTag);
  });

  if (condition.operator === 'and') {
    return results.every(r => r);
  }
  return results.some(r => r);
}
